// Commitment Kanban card — FE-042 / FE-043.
//
// Single Commitment surface inside a Kanban column. Mirrors the
// ``BeneCRM Tasks`` Pencil frame: rounded card with a subtle stroke,
// an optional left accent stripe (overdue → ember, blocked → gold),
// the title is a Link to the Commitment detail (FE-043), the Account
// name is a sibling Link (so no nested anchors), category + tag
// chips on a wrap row, and a footer with the due date + assignee
// initials.

import { Calendar, ClockAlert, OctagonX } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { CommitmentKanbanMoveToMenu } from "./CommitmentKanbanMoveToMenu";
import { SearchableSelect } from "./SearchableSelect";
import type {
  CommitmentListRow,
  CommitmentPriority,
  CommitmentStatus,
} from "./types";


export interface CommitmentKanbanCardProps {
  row: CommitmentListRow;
  /** Mounted with movement enabled when the parent provides a
   * handler (the board does; tests that mount the card in
   * isolation can omit it and the kebab menu / drag affordance
   * stay disabled). */
  onMove?: (commitmentId: string, toStatus: CommitmentStatus) => void;
  /** Inline assignee change from the kanban card. ``null``
   * clears assignment. */
  onAssign?: (commitmentId: string, userId: string | null) => void;
  /** Inline due-date change. ``null`` clears the due. */
  onSetDueDate?: (commitmentId: string, isoOrNull: string | null) => void;
  /** Members for the inline assignee picker. */
  members?: import("./api").MemberPickerRow[];
  visibleStatuses?: CommitmentStatus[];
  isPending?: boolean;
  /** Router location state stamped on the detail link so the
   * detail page's Back link can return to the caller's filtered
   * URL. */
  linkState?: unknown;
}


export const KANBAN_DRAG_MIME = "application/x-benecrm-commitment";


export function CommitmentKanbanCard({
  row, onMove, onAssign, onSetDueDate, members,
  visibleStatuses, isPending, linkState,
}: CommitmentKanbanCardProps) {
  const overdue = isOverdue(row);
  const accountName = row.account_name ?? "Unknown account";
  const stripeClass = overdue
    ? "bg-ember"
    : row.is_blocked
      ? "bg-gold"
      : null;
  const draggable = Boolean(onMove) && !isPending;
  return (
    <article
      data-testid={`commitments-card-${row.commitment_id}`}
      data-status={row.status}
      data-overdue={overdue ? "true" : "false"}
      data-blocked={row.is_blocked ? "true" : "false"}
      data-draggable={draggable ? "true" : "false"}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        // Pack the id and the originating status into the drag
        // payload. Columns read both back so the drop handler can
        // (a) skip same-column drops without a re-render and
        // (b) route Cancelled targets through the reason modal.
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData(
          KANBAN_DRAG_MIME,
          JSON.stringify({
            commitment_id: row.commitment_id,
            from_status: row.status,
          }),
        );
        // Setting ``text/plain`` keeps Firefox + Safari happy when
        // the custom MIME isn't echoed by the browser's drag image
        // handler. The columns prefer the structured key but fall
        // back to this shape.
        e.dataTransfer.setData("text/plain", row.commitment_id);
      }}
      className="relative flex flex-col gap-1 overflow-hidden rounded-xl border border-border-subtle bg-raised px-3 py-2 shadow-sm transition-fast ease-out hover:border-border-strong data-[draggable=true]:cursor-grab"
    >
      {stripeClass && (
        <span
          aria-hidden="true"
          className={[
            "absolute left-0 top-0 h-full w-1",
            stripeClass,
          ].join(" ")}
        />
      )}

      <div className="flex items-start justify-between gap-2">
        <span className="flex min-w-0 flex-col gap-0.5">
          <Link
            to={`/dashboard/commitments/${row.commitment_id}`}
            state={linkState}
            data-testid={`commitments-card-detail-link-${row.commitment_id}`}
            className="line-clamp-2 text-sm font-medium leading-snug text-text-primary underline decoration-border-subtle decoration-1 underline-offset-4 hover:decoration-text-primary"
          >
            {row.title}
          </Link>
          <span
            data-testid={`commitments-card-number-${row.commitment_id}`}
            className="text-[10px] font-medium uppercase tracking-wide text-text-tertiary"
          >
            {row.commitment_number}
          </span>
        </span>
        <div className="flex items-center gap-1">
          <CornerMeta
            priority={row.priority}
            complexity={row.complexity}
          />
          {onMove && (
            <CommitmentKanbanMoveToMenu
              commitmentId={row.commitment_id}
              fromStatus={row.status}
              visibleStatuses={visibleStatuses}
              onMove={(to) => onMove(row.commitment_id, to)}
              disabled={isPending}
            />
          )}
        </div>
      </div>

      <Link
        to={`/dashboard/accounts/${row.client_id}`}
        data-testid={`commitments-card-account-link-${row.commitment_id}`}
        // ``no-underline`` on the focus state too — the persistent
        // underline that we kept seeing on the screenshot was the
        // browser's default anchor decoration leaking through after
        // a click. ``focus:no-underline`` + ``focus-visible:ring``
        // gives keyboard users a visible focus state without the
        // sticky underline.
        className="text-xs text-text-secondary no-underline outline-none transition-fast ease-out hover:text-text-primary hover:underline focus:no-underline focus-visible:ring-1 focus-visible:ring-teal/50"
      >
        {accountName}
      </Link>

      {/* Tags row — render only when at least one tag is set, so
          empty cards don't carry an extra gap-row of whitespace. */}
      {row.tags.length > 0 ? (
        <ul
          data-testid={`commitments-card-tags-${row.commitment_id}`}
          role="list"
          className="flex flex-wrap gap-1"
        >
          {row.tags.slice(0, 3).map((tag) => (
            <li key={tag.id}>
              <span
                className={[
                  "inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium",
                  tag.is_regulatory
                    ? "bg-ember-tint text-ember"
                    : "bg-recessed text-text-secondary",
                ].join(" ")}
              >
                {tag.name}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        // Keep the testid alive for tests that look for an empty list.
        <ul
          data-testid={`commitments-card-tags-${row.commitment_id}`}
          role="list"
          className="hidden"
        />
      )}

      <footer className="mt-0.5 flex items-center justify-between gap-2 text-[11px]">
        <div className="flex items-center gap-2 text-text-tertiary">
          {onSetDueDate ? (
            <CardDueDate
              row={row} overdue={overdue}
              onSet={(iso) => onSetDueDate(row.commitment_id, iso)}
            />
          ) : row.due_date_time ? (
            <span
              data-testid={`commitments-card-due-${row.commitment_id}`}
              className={[
                "whitespace-nowrap",
                overdue ? "font-medium text-ember" : "",
              ].join(" ")}
            >
              {formatShortDate(row.due_date_time)}
            </span>
          ) : (
            <span
              data-testid={`commitments-card-due-empty-${row.commitment_id}`}
              role="img"
              aria-label="No due date"
              title="No due date"
              className="inline-flex h-5 w-5 items-center justify-center text-text-tertiary"
            >
              <Calendar size={14} aria-hidden="true" />
            </span>
          )}
          {overdue && (
            <span
              data-testid={`commitments-card-overdue-${row.commitment_id}`}
              role="img"
              aria-label="Overdue"
              title={overdueTitle(row.due_date_time)}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-ember-tint text-ember"
            >
              <ClockAlert size={12} aria-hidden="true" />
            </span>
          )}
          {row.is_blocked && (
            <span
              data-testid={`commitments-card-blocked-${row.commitment_id}`}
              role="img"
              aria-label="Blocked"
              title={blockedTitle(row.blocked_reason)}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-recessed text-blocked"
            >
              <OctagonX size={12} aria-hidden="true" />
            </span>
          )}
        </div>
        {onAssign && members !== undefined ? (
          <CardAssignee
            row={row}
            members={members}
            onPick={(userId) => onAssign(row.commitment_id, userId)}
          />
        ) : (
          <AssigneeChip row={row} />
        )}
      </footer>
    </article>
  );
}


function AssigneeChip({ row }: { row: CommitmentListRow }) {
  const name = row.assigned_to_display_name || row.assigned_to_email;
  if (!name) {
    return (
      <span
        data-testid={`commitments-card-unassigned-${row.commitment_id}`}
        className="text-[11px] italic text-text-tertiary"
      >
        Unassigned
      </span>
    );
  }
  const initials = computeInitials(name);
  return (
    <span
      data-testid={`commitments-card-assignee-${row.commitment_id}`}
      title={name}
      className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sage-tint text-[10px] font-semibold text-teal"
    >
      {initials}
    </span>
  );
}


function computeInitials(name: string): string {
  const cleaned = name.trim();
  if (cleaned === "") return "?";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    // Email-ish or single token — first two characters.
    return cleaned.slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


function CardDueDate({
  row, overdue, onSet,
}: {
  row: CommitmentListRow;
  overdue: boolean;
  onSet: (isoOrNull: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const initialLocal = row.due_date_time
    ? toDatetimeLocalShort(row.due_date_time) : "";
  if (editing) {
    return (
      <input
        type="datetime-local"
        autoFocus
        data-testid={`commitments-card-due-input-${row.commitment_id}`}
        defaultValue={initialLocal}
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => {
          const next = (e.target as HTMLInputElement).value;
          setEditing(false);
          if (next === initialLocal) return;
          onSet(next ? new Date(next).toISOString() : null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setEditing(false);
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
        className="rounded border border-border-subtle bg-raised px-1 py-0.5 text-[11px] text-text-primary"
      />
    );
  }
  return (
    <button
      type="button"
      data-testid={`commitments-card-due-trigger-${row.commitment_id}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setEditing(true);
      }}
      className={[
        "inline-flex items-center rounded px-1 py-0.5 hover:bg-recessed",
        overdue ? "font-medium text-ember" : "text-text-tertiary",
      ].join(" ")}
      aria-label={row.due_date_time ? undefined : "Set due date"}
      title={row.due_date_time ? undefined : "Set due date"}
    >
      {row.due_date_time ? (
        <span
          data-testid={`commitments-card-due-${row.commitment_id}`}
          className="whitespace-nowrap"
        >
          {formatShortDate(row.due_date_time)}
        </span>
      ) : (
        <Calendar
          data-testid={`commitments-card-due-empty-${row.commitment_id}`}
          size={14}
          aria-hidden="true"
        />
      )}
    </button>
  );
}


function toDatetimeLocalShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}


function CardAssignee({
  row, members, onPick,
}: {
  row: CommitmentListRow;
  members: import("./api").MemberPickerRow[];
  onPick: (userId: string | null) => void;
}) {
  const display = row.assigned_to_display_name
    || row.assigned_to_email
    || "Unassigned";
  const initials = computeInitials(display);
  return (
    <SearchableSelect
      fieldId={`card-assignee-${row.commitment_id}`}
      value={row.assigned_to ?? ""}
      options={[
        { value: "", label: "Unassigned", isClear: true },
        ...members.map((m) => ({
          value: m.id,
          label: m.display_name || m.email,
          hint: m.email,
        })),
      ]}
      triggerDisplay={
        display === "Unassigned" ? (
          <span
            data-testid={`commitments-card-unassigned-${row.commitment_id}`}
            className="text-[11px] italic text-text-tertiary"
          >
            Unassigned
          </span>
        ) : (
          <span
            data-testid={`commitments-card-assignee-${row.commitment_id}`}
            title={display}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sage-tint text-[10px] font-semibold text-teal"
          >
            {initials}
          </span>
        )
      }
      onPick={(userId) => onPick(userId || null)}
    />
  );
}


function isOverdue(row: CommitmentListRow): boolean {
  if (!row.due_date_time) return false;
  if (row.status === "done" || row.status === "cancelled") return false;
  const due = new Date(row.due_date_time).getTime();
  if (Number.isNaN(due)) return false;
  return due < Date.now();
}


function overdueTitle(due: string | null): string {
  if (!due) return "Overdue";
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return "Overdue";
  return `Overdue since ${d.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  })}`;
}


function blockedTitle(reason: string | null): string {
  return reason ? `Blocked: ${reason}` : "Blocked";
}


// Compact corner meta — small priority dot + a single complexity
// letter. Replaces the wider "Medium / Large / X-Large" pill so the
// card title gets the full content column. Hover/title carries the
// long-form labels for users who need them.
const PRIORITY_DOT: Record<CommitmentPriority, string> = {
  high: "bg-ember",
  medium: "bg-gold",
  low: "bg-text-tertiary",
};
const PRIORITY_LABEL: Record<CommitmentPriority, string> = {
  high: "High", medium: "Medium", low: "Low",
};


function complexityLetter(complexity: number | null): string | null {
  switch (complexity) {
    case 1: return "S";
    case 2: return "M";
    case 3: return "L";
    case 5: return "XL";
    default: return null;
  }
}


function complexityTitle(complexity: number | null): string {
  switch (complexity) {
    case 1: return "Small (1 pt)";
    case 2: return "Medium (2 pts)";
    case 3: return "Large (3 pts)";
    case 5: return "X-Large (5 pts)";
    default: return "Unsized";
  }
}


function CornerMeta({
  priority, complexity,
}: { priority: CommitmentPriority; complexity: number | null }) {
  const letter = complexityLetter(complexity);
  const tooltip = `${PRIORITY_LABEL[priority]} priority • ${complexityTitle(complexity)}`;
  return (
    <span
      data-testid={`commitments-card-corner-${priority}`}
      data-priority={priority}
      data-complexity={complexity ?? ""}
      title={tooltip}
      className="inline-flex items-center gap-1 text-xs font-medium text-text-tertiary"
    >
      <span
        aria-hidden="true"
        className={[
          "inline-block h-1.5 w-1.5 rounded-full",
          PRIORITY_DOT[priority],
        ].join(" ")}
      />
      {letter && <span>{letter}</span>}
    </span>
  );
}


function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric",
  });
}
